import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import financialService from '../../services/financialService';
import './FinancialDashboard.css';

const DistributionCharts = ({ marketplace, cacheKey, horizonteTemporal = 'Mensual', tipoVolumen = 'Predicciones' }) => {
  const [loading, setLoading] = useState(true);
  const [distributionData, setDistributionData] = useState(null);
  const [nbins, setNbins] = useState(30);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadDistribution();
  }, [marketplace, cacheKey, nbins, horizonteTemporal, tipoVolumen]);
  
  const loadDistribution = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await financialService.getDistributionCharts(
        marketplace, 
        cacheKey, 
        nbins,
        horizonteTemporal,
        tipoVolumen
      );
      
      if (result.success) {
        setDistributionData(result);
      }
    } catch (err) {
      console.error('Error cargando distribución:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando distribución...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <p>❌ {error}</p>
        <button onClick={loadDistribution} className="btn btn-primary">
          🔄 Reintentar
        </button>
      </div>
    );
  }
  
  if (!distributionData) {
    return <div className="no-data">No hay datos disponibles</div>;
  }
  
  const stats = distributionData.descriptive_stats;
  
  return (
    <div className="distribution-charts">
      <div className="charts-header">
        <h3>📈 Distribución de Utilidad Neta</h3>
        <div className="chart-controls">
          <label>Número de bins:</label>
          <input 
            type="number" 
            value={nbins} 
            onChange={(e) => {
              const val = parseInt(e.target.value);
              if (val >= 10 && val <= 100) {
                setNbins(val);
              }
            }}
            min="10"
            max="100"
            className="nbins-input"
          />
          <span className="control-hint">(10-100)</span>
        </div>
      </div>
      
      <div className="chart-section">
        <div className="chart-container">
          <Plot
            data={distributionData.histogram_chart.data}
            layout={{
              ...distributionData.histogram_chart.layout,
              autosize: true
            }}
            config={{
              responsive: true,
              displayModeBar: true,
              modeBarButtonsToRemove: ['pan2d', 'lasso2d'],
              displaylogo: false
            }}
            style={{ width: '100%', height: '500px' }}
            useResizeHandler={true}
          />
        </div>
        
        {distributionData.kde_curve ? (
          <div className="kde-info">
            ✅ <strong>Curva de densidad KDE incluida</strong> (línea roja suave sobre el histograma)
          </div>
        ) : (
          <div className="kde-warning">
            ⚠️ Curva KDE no disponible (requiere scipy en el servidor)
          </div>
        )}
      </div>
      
      <div className="stats-section">
        <h4>📊 Estadísticas Descriptivas</h4>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Count</div>
            <div className="stat-value">{stats.count.toLocaleString()}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">Media</div>
            <div className="stat-value">${stats.mean.toFixed(2)}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">Mediana</div>
            <div className="stat-value">${stats.median.toFixed(2)}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">Desv. Estándar</div>
            <div className="stat-value">${stats.std.toFixed(2)}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">Mínimo</div>
            <div className="stat-value negative">${stats.min.toFixed(2)}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">Q1 (25%)</div>
            <div className="stat-value">${stats.q1.toFixed(2)}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">Q3 (75%)</div>
            <div className="stat-value">${stats.q3.toFixed(2)}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">Máximo</div>
            <div className="stat-value positive">${stats.max.toFixed(2)}</div>
          </div>
        </div>
      </div>
      
      <div className="insights-section">
        <div className="insight-card">
          <div className="insight-icon">💡</div>
          <div className="insight-content">
            <strong>Interpretación:</strong>
            <ul>
              <li>La <strong>mediana</strong> (${stats.median.toFixed(2)}) representa el punto medio de la distribución</li>
              <li>El <strong>50% de los productos</strong> tienen utilidad entre ${stats.q1.toFixed(2)} y ${stats.q3.toFixed(2)}</li>
              <li>{stats.min < 0 ? `Hay productos en pérdida (mínimo: $${stats.min.toFixed(2)})` : 'Todos los productos son rentables'}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistributionCharts;

